import { text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { EMPTY_STR } from "@/constants/string";
import { CASE_MATCH_TAG_COLOR_MAP, TCaseMatchTagColor } from "@/features/case/constants/case-match-tag-colors";
import { useTags } from "@/api/services/main/tag";
import { Box, Chip, Popover, Stack, SxProps } from "@mui/material";
import { FC, MouseEvent, useMemo, useState } from "react";

export type TCaseMatchTagListProps = {
  tagIds?: string[] | null | undefined;
  maxVisible?: number;
  // Off inside table cells (a wrapped chip overflows the fixed row height); on in full-width detail panels.
  wrap?: boolean;
};

const BASE_CHIP_SX: SxProps = {
  fontWeight: "700",
  fontSize: "12px",
  borderRadius: rounded.md,
  flexShrink: 0,
};

const MORE_CHIP_SX: SxProps = {
  ...BASE_CHIP_SX,
  bgcolor: "#E6E7E8",
  color: text.secondary,
  cursor: "pointer",
};

const getTagChipSx = (color?: TCaseMatchTagColor): SxProps => ({
  ...BASE_CHIP_SX,
  bgcolor: color?.bg,
  color: color?.border,
  border: "1px solid",
  borderColor: color?.border,
});

export const CaseMatchTagList: FC<TCaseMatchTagListProps> = ({ tagIds, maxVisible, wrap = false }) => {
  const { data: caseMatchTags } = useTags("case-match");
  // tags.id is bigint (typed number, runtime string); key by String so it matches string tagIds.
  const tagMap = useMemo(() => new Map(caseMatchTags.map((t) => [String(t.id), t])), [caseMatchTags]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const ids = useMemo(() => {
    const list = Array.isArray(tagIds) ? [...tagIds] : [];
    return Array.from(new Set(list))
      .filter((id) => tagMap.has(id))
      .sort((a, b) => (tagMap.get(a)?.order ?? 0) - (tagMap.get(b)?.order ?? 0));
  }, [tagIds, tagMap]);

  if (ids.length === 0) return <>{EMPTY_STR.TEXT}</>;

  const collapse = maxVisible != null && ids.length > maxVisible;
  const visible = collapse ? ids.slice(0, maxVisible) : ids;
  const hiddenTags = collapse ? ids.slice(maxVisible) : [];

  const handleOpenMore = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleCloseMore = () => setAnchorEl(null);

  const renderItem = (id: string) => {
    const tagItem = tagMap.get(id);
    const color = tagItem ? CASE_MATCH_TAG_COLOR_MAP[tagItem.color] : undefined;
    return <Chip key={id} label={tagItem?.label ?? id} sx={getTagChipSx(color)} />;
  };

  return (
    <Stack direction="row" useFlexGap flexWrap={wrap ? "wrap" : "nowrap"} gap={1}>
      {visible.map(renderItem)}
      {hiddenTags.length > 0 && (
        <>
          <Chip label={`+${hiddenTags.length}`} sx={MORE_CHIP_SX} onClick={handleOpenMore} />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseMore}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Box sx={{ p: 1.25, maxWidth: 280 }}>
              <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
                {hiddenTags.map(renderItem)}
              </Stack>
            </Box>
          </Popover>
        </>
      )}
    </Stack>
  );
};
